import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { format, parse } from "date-fns";
import BatchType from "../../../types/Batch";
import ProgressBar from "../../batches/components/ProgressBar";
import { analyseTheme } from "../../analyse/analyseTheme";
import withContext from "../../../tools/withContext";
import {
  buildAssignedBatchesPath,
  selectBatches,
} from "../hooks/batchQueries";
import { useWidgetQuery } from "../hooks/useWidgetQuery";
import { panelCardStyles, secondarySoft } from "./sharedStyles";
import StatusChip from "./StatusChip";
import DeadlineDisplay from "./DeadlineDisplay";
import LabelingTypeIcon from "./LabelingTypeIcon";
import { canLabelizeBatch, getLabelerPath, getBatchLabelingType } from "./utils";
import { AssignedBatchesWidgetSkeleton } from "./WidgetSkeletons";

const EMPTY_BATCHES: BatchType[] = [];

const styles = (theme: Theme) =>
  createStyles({
    ...panelCardStyles(theme),
    batchRow: {
      backgroundColor: analyseTheme.surface,
      border: `1px solid ${analyseTheme.border}`,
      borderRadius: analyseTheme.radiusSm,
      padding: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      textDecoration: "none",
      color: "inherit",
      transition: "border-color 0.2s",
      "&:hover": {
        borderColor: theme.palette.secondary.light,
        backgroundColor: secondarySoft,
      },
    },
    batchRowDisabled: {
      cursor: "default",
      "&:hover": {
        borderColor: analyseTheme.border,
      },
    },
    batchInfo: {
      flex: 1,
      minWidth: 0,
      paddingRight: theme.spacing.unit * 2,
    },
    batchCreatedDate: {
      color: analyseTheme.textSecondary,
      fontSize: "0.8rem",
      marginBottom: 2,
    },
    batchNameRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      minWidth: 0,
    },
    batchTypeIcon: {
      color: analyseTheme.textSecondary,
      flexShrink: 0,
    },
    batchName: {
      flex: 1,
      minWidth: 0,
      fontWeight: 600,
      fontSize: "1rem",
      color: analyseTheme.textPrimary,
    },
    batchProject: {
      color: analyseTheme.textSecondary,
      fontSize: "0.8rem",
      fontStyle: "italic",
    },
    batchMeta: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "8px",
      marginTop: 6,
    },
    batchProgress: {
      width: 180,
      flexShrink: 0,
      margin: "0 8px",
      "& > div": {
        padding: "0 !important",
      },
    },
    batchRowChevron: {
      width: 32,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    emptyText: {
      color: analyseTheme.textSecondary,
      fontStyle: "italic",
    },
  });

interface Props {
  classes: Record<string, string>;
  isAdmin: boolean;
  onBatchNavigate: (batchId: number) => void;
  ctx: { appFetch: (url: string) => Promise<any>; user: { id: number } };
}

const AssignedBatchesWidget = ({
  classes,
  isAdmin,
  onBatchNavigate,
  ctx: { appFetch, user },
}: Props) => {
  const path = useMemo(
    () => (user?.id != null ? buildAssignedBatchesPath(user.id) : null),
    [user?.id],
  );

  const { isLoading, data: assignedBatches } = useWidgetQuery(
    path,
    appFetch,
    selectBatches,
    EMPTY_BATCHES,
  );

  if (isLoading) {
    return <AssignedBatchesWidgetSkeleton />;
  }

  const viewAllHref =
    isAdmin && assignedBatches.length > 0
      ? `/batches/0?userId=${user.id}&size=20&sort=createdDate,desc`
      : "/batches/0?sort=createdDate,desc";

  return (
    <>
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionHeaderTitle} variant="h6">
          Mes lots assignés
        </Typography>
        <Link to={viewAllHref} className={classes.sectionLink}>
          Afficher tout
        </Link>
      </div>
      {assignedBatches.length === 0 ? (
        <Typography className={classes.emptyText}>
          Aucun lot assigné pour le moment.
        </Typography>
      ) : (
        assignedBatches.map((batch) => {
          const userRole = (user as { authorities?: string[] })?.authorities?.[0];
          const labelizable = canLabelizeBatch(batch, userRole);
          const rowContent = (
            <>
              <div className={classes.batchInfo}>
                <div className={classes.batchNameRow}>
                  <LabelingTypeIcon
                    labelingType={getBatchLabelingType(batch)}
                    className={classes.batchTypeIcon}
                  />
                  <Typography className={classes.batchName} noWrap>
                    {batch.name}
                  </Typography>
                </div>
                <Typography className={classes.batchProject} noWrap>
                  {batch.project?.name}
                </Typography>
                <div className={classes.batchMeta}>
                  {batch.createdDate && (
                    <Typography className={classes.batchCreatedDate} noWrap>
                      {format(
                        parse(new Date(batch.createdDate)),
                        "DD/MM/YY HH:mm",
                      )}
                    </Typography>
                  )}
                  <StatusChip state={batch.state} />
                  <DeadlineDisplay deadline={batch.deadline} />
                </div>
              </div>
              <div className={classes.batchProgress}>
                <ProgressBar
                  totalLabelDocs={batch.totalLabelDocs}
                  totalLabelDocsByModel={batch.totalLabelDocsByModel}
                  totalEligibleDocs={batch.totalEligibleDocs}
                  isVideo={getBatchLabelingType(batch) === "VIDEO"}
                />
              </div>
              <div className={classes.batchRowChevron}>
                {labelizable && (
                  <ChevronRightIcon
                    style={{ color: analyseTheme.textSecondary }}
                  />
                )}
              </div>
            </>
          );

          if (labelizable) {
            return (
              <a
                key={batch.id}
                href={getLabelerPath(batch)}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.batchRow}
                onMouseDown={() => onBatchNavigate(batch.id)}
              >
                {rowContent}
              </a>
            );
          }

          return (
            <div
              key={batch.id}
              className={`${classes.batchRow} ${classes.batchRowDisabled}`}
            >
              {rowContent}
            </div>
          );
        })
      )}
    </>
  );
};

export default withContext(withStyles(styles)(AssignedBatchesWidget));
